export type GiftProductType = 'forfait' | 'credit_pack' | string;

export type GiftViewerRole = 'anonymous' | 'recipient' | 'purchaser' | 'claimant' | string;

export type GiftNextSteps = {
  use_path?: string | null;
  use_label?: string | null;
  wallet_path?: string | null;
  wallet_label?: string | null;
};

export interface GiftPublicView {
  code: string;
  status: string;
  product_type: GiftProductType;
  product_name: string;
  credits_amount?: number | null;
  recipient_name?: string | null;
  personal_message?: string | null;
  expires_at?: string | null;
  redeemed_at?: string | null;
  remaining_sessions?: number | null;
  credits_remaining?: number | null;
  can_redeem?: boolean;
  viewer_role?: GiftViewerRole | null;
  viewer_can_redeem?: boolean;
  public_url?: string;
  next?: GiftNextSteps | null;
  pro?: {
    name?: string;
    slug?: string;
    logo_url?: string;
    city?: string;
  };
}

export const GIFT_STATUS_LABEL: Record<string, string> = {
  ready: 'À réclamer',
  sent: 'À réclamer',
  scheduled: 'Envoi programmé',
  redeemed: 'Sur votre compte',
  cancelled: 'Annulée',
  expired: 'Expirée',
};

export type GiftUiLang = 'fr' | 'en' | 'de' | 'es' | 'it' | 'nl';

export function resolveGiftUiLang(lang?: string | null): GiftUiLang {
  const raw = String(lang || '').toLowerCase();
  if (raw.startsWith('nl')) return 'nl';
  if (raw.startsWith('de')) return 'de';
  if (raw.startsWith('es')) return 'es';
  if (raw.startsWith('it')) return 'it';
  if (raw.startsWith('en')) return 'en';
  return 'fr';
}

const DATE_LOCALE: Record<GiftUiLang, string> = {
  fr: 'fr-FR',
  en: 'en-GB',
  de: 'de-DE',
  es: 'es-ES',
  it: 'it-IT',
  nl: 'nl-NL',
};

const STATUS_I18N: Record<GiftUiLang, Record<string, string>> = {
  fr: GIFT_STATUS_LABEL,
  en: {
    ready: 'To claim',
    sent: 'To claim',
    scheduled: 'Scheduled send',
    redeemed: 'On your account',
    cancelled: 'Cancelled',
    expired: 'Expired',
  },
  de: {
    ready: 'Einzulösen',
    sent: 'Einzulösen',
    scheduled: 'Geplanter Versand',
    redeemed: 'Auf Ihrem Konto',
    cancelled: 'Storniert',
    expired: 'Abgelaufen',
  },
  es: {
    ready: 'Por canjear',
    sent: 'Por canjear',
    scheduled: 'Envío programado',
    redeemed: 'En tu cuenta',
    cancelled: 'Anulada',
    expired: 'Caducada',
  },
  it: {
    ready: 'Da riscattare',
    sent: 'Da riscattare',
    scheduled: 'Invio programmato',
    redeemed: 'Sul tuo account',
    cancelled: 'Annullata',
    expired: 'Scaduta',
  },
  nl: {
    ready: 'Te claimen',
    sent: 'Te claimen',
    scheduled: 'Geplande verzending',
    redeemed: 'Op je account',
    cancelled: 'Geannuleerd',
    expired: 'Verlopen',
  },
};

export function giftStatusLabel(status: string, lang?: string | null): string {
  return STATUS_I18N[resolveGiftUiLang(lang)][status] || status;
}

export function isGiverExperience(opts: {
  print: boolean;
  viewerRole?: GiftViewerRole | null;
}): boolean {
  if (opts.print) return true;
  return opts.viewerRole === 'purchaser';
}

export function isWrongAccountViewer(view: GiftPublicView): boolean {
  return (
    view.viewer_role === 'claimant' &&
    (view.status === 'ready' || view.status === 'sent')
  );
}

export function viewerCanRedeem(view: GiftPublicView): boolean {
  if (typeof view.viewer_can_redeem === 'boolean') {
    return view.viewer_can_redeem;
  }
  return Boolean(view.can_redeem);
}

export function formatGiftDate(
  iso?: string | null,
  withTime = true,
  lang?: string | null
): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const locale = DATE_LOCALE[resolveGiftUiLang(lang)];
  return date.toLocaleString(locale, withTime
    ? { dateStyle: 'long', timeStyle: 'short' }
    : { dateStyle: 'long' });
}

export function giftExpiryCopy(
  expiresAt?: string | null,
  status?: string,
  lang?: string | null
): string | null {
  if (!expiresAt) return null;
  const formatted = formatGiftDate(expiresAt, true, lang);
  if (!formatted) return null;
  const l = resolveGiftUiLang(lang);
  if (status === 'expired') {
    const prefix = {
      fr: 'Expirée le',
      en: 'Expired on',
      de: 'Abgelaufen am',
      es: 'Caducada el',
      it: 'Scaduta il',
      nl: 'Verlopen op',
    }[l];
    return `${prefix} ${formatted}`;
  }
  if (status === 'redeemed' || status === 'cancelled') return null;
  const prefix = {
    fr: 'Valable jusqu’au',
    en: 'Valid until',
    de: 'Gültig bis',
    es: 'Válida hasta el',
    it: 'Valida fino al',
    nl: 'Geldig tot',
  }[l];
  return `${prefix} ${formatted}`;
}

export function giftProductSummary(view: GiftPublicView, lang?: string | null): string {
  if (view.product_type === 'credit_pack' && view.credits_amount != null) {
    const suffix = {
      fr: '€ de crédits',
      en: '€ in credits',
      de: '€ Guthaben',
      es: '€ en créditos',
      it: '€ di crediti',
      nl: '€ credits',
    }[resolveGiftUiLang(lang)];
    return `${view.credits_amount} ${suffix}`;
  }
  return view.product_name;
}

function proFallback(lang?: string | null, possessive = false): string {
  const l = resolveGiftUiLang(lang);
  if (possessive) {
    return {
      fr: 'Votre professionnel',
      en: 'Your professional',
      de: 'Ihr Profi',
      es: 'Tu profesional',
      it: 'Il tuo professionista',
      nl: 'Je professional',
    }[l];
  }
  return {
    fr: 'votre professionnel',
    en: 'your professional',
    de: 'Ihrem Profi',
    es: 'tu profesional',
    it: 'il tuo professionista',
    nl: 'je professional',
  }[l];
}

export function recipientHeadline(
  view: GiftPublicView,
  opts: { justRedeemed?: boolean; lang?: string | null } = {}
): { title: string; lede: string } {
  const lang = resolveGiftUiLang(opts.lang);
  const proName = view.pro?.name || proFallback(lang, true);
  const product = view.product_name || {
    fr: 'un cadeau',
    en: 'a gift',
    de: 'ein Geschenk',
    es: 'un regalo',
    it: 'un regalo',
    nl: 'een cadeau',
  }[lang];

  if (opts.justRedeemed || view.status === 'redeemed') {
    if (view.product_type === 'credit_pack') {
      return {
        title: {
          fr: 'Vos crédits sont à vous',
          en: 'Your credits are yours',
          de: 'Ihr Guthaben gehört Ihnen',
          es: 'Tus créditos ya son tuyos',
          it: 'I crediti sono tuoi',
          nl: 'Je credits zijn van jou',
        }[lang],
        lede: `${giftProductSummary(view, lang)} ${
          { fr: 'chez', en: 'at', de: 'bei', es: 'en', it: 'da', nl: 'bij' }[lang]
        } ${proName}`,
      };
    }
    return {
      title: {
        fr: 'Votre forfait est à vous',
        en: 'Your package is yours',
        de: 'Ihr Paket gehört Ihnen',
        es: 'Tu bono ya es tuyo',
        it: 'Il tuo pacchetto è tuo',
        nl: 'Je abonnement is van jou',
      }[lang],
      lede: {
        fr: `${product} est maintenant sur votre compte ${proName}.`,
        en: `${product} is now on your ${proName} account.`,
        de: `${product} ist jetzt auf Ihrem Konto bei ${proName}.`,
        es: `${product} ya está en tu cuenta de ${proName}.`,
        it: `${product} è ora sul tuo account ${proName}.`,
        nl: `${product} staat nu op je account bij ${proName}.`,
      }[lang],
    };
  }

  if (isWrongAccountViewer(view)) {
    return {
      title: {
        fr: 'Ce cadeau n’est pas pour ce compte',
        en: 'This gift is not for this account',
        de: 'Dieses Geschenk ist nicht für dieses Konto',
        es: 'Este regalo no es para esta cuenta',
        it: 'Questo regalo non è per questo account',
        nl: 'Dit cadeau is niet voor dit account',
      }[lang],
      lede: {
        fr: `Connectez-vous avec l’e-mail du destinataire pour réclamer ${product}.`,
        en: `Sign in with the recipient’s email to claim ${product}.`,
        de: `Melden Sie sich mit der E-Mail der empfangenden Person an, um ${product} einzulösen.`,
        es: `Inicia sesión con el correo del destinatario para canjear ${product}.`,
        it: `Accedi con l’e-mail del destinatario per riscattare ${product}.`,
        nl: `Meld je aan met het e-mailadres van de ontvanger om ${product} te claimen.`,
      }[lang],
    };
  }

  if (view.status === 'expired') {
    return {
      title: {
        fr: 'Cette carte n’est plus valable',
        en: 'This card is no longer valid',
        de: 'Diese Karte ist nicht mehr gültig',
        es: 'Esta tarjeta ya no es válida',
        it: 'Questa carta non è più valida',
        nl: 'Deze kaart is niet meer geldig',
      }[lang],
      lede: {
        fr: `${product} chez ${proName} ne peut plus être réclamé.`,
        en: `${product} at ${proName} can no longer be claimed.`,
        de: `${product} bei ${proName} kann nicht mehr eingelöst werden.`,
        es: `${product} en ${proName} ya no se puede canjear.`,
        it: `${product} da ${proName} non può più essere riscattato.`,
        nl: `${product} bij ${proName} kan niet meer worden geclaimd.`,
      }[lang],
    };
  }

  if (view.status === 'cancelled') {
    return {
      title: {
        fr: 'Cette carte a été annulée',
        en: 'This card has been cancelled',
        de: 'Diese Karte wurde storniert',
        es: 'Esta tarjeta ha sido anulada',
        it: 'Questa carta è stata annullata',
        nl: 'Deze kaart is geannuleerd',
      }[lang],
      lede: {
        fr: `${product} chez ${proName} n’est plus disponible.`,
        en: `${product} at ${proName} is no longer available.`,
        de: `${product} bei ${proName} ist nicht mehr verfügbar.`,
        es: `${product} en ${proName} ya no está disponible.`,
        it: `${product} da ${proName} non è più disponibile.`,
        nl: `${product} bij ${proName} is niet meer beschikbaar.`,
      }[lang],
    };
  }

  if (view.status === 'scheduled') {
    return {
      title: {
        fr: 'Cadeau en cours d’envoi',
        en: 'Gift being sent',
        de: 'Geschenk wird gesendet',
        es: 'Regalo en envío',
        it: 'Regalo in invio',
        nl: 'Cadeau wordt verzonden',
      }[lang],
      lede: {
        fr: `${proName} vous prépare ${product}.`,
        en: `${proName} is preparing ${product} for you.`,
        de: `${proName} bereitet ${product} für Sie vor.`,
        es: `${proName} te prepara ${product}.`,
        it: `${proName} ti sta preparando ${product}.`,
        nl: `${proName} maakt ${product} voor je klaar.`,
      }[lang],
    };
  }

  return {
    title: {
      fr: 'Un cadeau pour vous',
      en: 'A gift for you',
      de: 'Ein Geschenk für Sie',
      es: 'Un regalo para ti',
      it: 'Un regalo per te',
      nl: 'Een cadeau voor jou',
    }[lang],
    lede: {
      fr: `${proName} vous offre ${product}`,
      en: `${proName} is giving you ${product}`,
      de: `${proName} schenkt Ihnen ${product}`,
      es: `${proName} te ofrece ${product}`,
      it: `${proName} ti offre ${product}`,
      nl: `${proName} geeft je ${product}`,
    }[lang],
  };
}

export function recipientHowto(
  view: GiftPublicView,
  opts: { loggedIn: boolean; canRedeem: boolean; lang?: string | null }
): string | null {
  const lang = resolveGiftUiLang(opts.lang);
  const proName = view.pro?.name || proFallback(lang);
  const product = view.product_name || {
    fr: 'ce cadeau',
    en: 'this gift',
    de: 'dieses Geschenk',
    es: 'este regalo',
    it: 'questo regalo',
    nl: 'dit cadeau',
  }[lang];

  if (view.status === 'redeemed') {
    if (view.product_type === 'credit_pack') {
      return {
        fr: `À la prochaine réservation chez ${proName}, ces crédits s’appliquent automatiquement.`,
        en: `On your next booking with ${proName}, these credits apply automatically.`,
        de: `Bei der nächsten Buchung bei ${proName} wird dieses Guthaben automatisch verwendet.`,
        es: `En la próxima reserva con ${proName}, estos créditos se aplican solos.`,
        it: `Alla prossima prenotazione da ${proName}, questi crediti si applicano automaticamente.`,
        nl: `Bij je volgende boeking bij ${proName} worden deze credits automatisch toegepast.`,
      }[lang];
    }
    return {
      fr: `Réservez chez ${proName} : ${product} est déjà sur votre compte.`,
      en: `Book with ${proName}: ${product} is already on your account.`,
      de: `Buchen Sie bei ${proName}: ${product} ist bereits auf Ihrem Konto.`,
      es: `Reserva con ${proName}: ${product} ya está en tu cuenta.`,
      it: `Prenota da ${proName}: ${product} è già sul tuo account.`,
      nl: `Boek bij ${proName}: ${product} staat al op je account.`,
    }[lang];
  }

  if (isWrongAccountViewer(view)) {
    return {
      fr: `Ce code est réservé au compte du destinataire. Utilisez cet e-mail, puis réclamez ${product}.`,
      en: `This code is for the recipient’s account. Use that email, then claim ${product}.`,
      de: `Dieser Code ist für das Konto der empfangenden Person. Nutzen Sie diese E-Mail und lösen Sie ${product} ein.`,
      es: `Este código es para la cuenta del destinatario. Usa ese correo y canjea ${product}.`,
      it: `Questo codice è riservato all’account del destinatario. Usa quell’e-mail e riscatta ${product}.`,
      nl: `Deze code is voor het account van de ontvanger. Gebruik dat e-mailadres en claim ${product}.`,
    }[lang];
  }

  if (view.status === 'expired' || view.status === 'cancelled') {
    return null;
  }

  if (opts.loggedIn && opts.canRedeem) {
    return {
      fr: `Réclamez ${product} sur ce compte, puis réservez chez ${proName}.`,
      en: `Claim ${product} on this account, then book with ${proName}.`,
      de: `Lösen Sie ${product} auf diesem Konto ein und buchen Sie dann bei ${proName}.`,
      es: `Canjea ${product} en esta cuenta y reserva con ${proName}.`,
      it: `Riscatta ${product} su questo account, poi prenota da ${proName}.`,
      nl: `Claim ${product} op dit account en boek daarna bij ${proName}.`,
    }[lang];
  }

  if (!opts.loggedIn && opts.canRedeem) {
    return {
      fr: `Connectez-vous (ou créez un compte) pour ajouter ${product} à votre espace, puis réservez chez ${proName}.`,
      en: `Sign in (or create an account) to add ${product} to your space, then book with ${proName}.`,
      de: `Melden Sie sich an (oder erstellen Sie ein Konto), um ${product} hinzuzufügen, und buchen Sie bei ${proName}.`,
      es: `Inicia sesión (o crea una cuenta) para añadir ${product} a tu espacio y reserva con ${proName}.`,
      it: `Accedi (o crea un account) per aggiungere ${product} al tuo spazio, poi prenota da ${proName}.`,
      nl: `Meld je aan (of maak een account) om ${product} toe te voegen, en boek daarna bij ${proName}.`,
    }[lang];
  }

  return null;
}

export function giverHowto(view: GiftPublicView, lang?: string | null): string {
  const l = resolveGiftUiLang(lang);
  const proName = view.pro?.name || {
    fr: 'le professionnel',
    en: 'the professional',
    de: 'den Profi',
    es: 'el profesional',
    it: 'il professionista',
    nl: 'de professional',
  }[l];
  const recipient = view.recipient_name || {
    fr: 'le bénéficiaire',
    en: 'the recipient',
    de: 'die empfangende Person',
    es: 'el destinatario',
    it: 'il destinatario',
    nl: 'de ontvanger',
  }[l];
  return {
    fr:
      `Remettez cette carte à ${recipient}. ` +
      `La personne se connecte sur Rintintin avec son compte, réclame le cadeau, puis réserve chez ${proName}.`,
    en:
      `Give this card to ${recipient}. ` +
      `They sign in to Rintintin, claim the gift, then book with ${proName}.`,
    de:
      `Geben Sie diese Karte an ${recipient}. ` +
      `Die Person meldet sich bei Rintintin an, löst das Geschenk ein und bucht bei ${proName}.`,
    es:
      `Entrega esta tarjeta a ${recipient}. ` +
      `La persona inicia sesión en Rintintin, canjea el regalo y reserva con ${proName}.`,
    it:
      `Consegna questa carta a ${recipient}. ` +
      `La persona accede a Rintintin, riscatta il regalo e prenota da ${proName}.`,
    nl:
      `Geef deze kaart aan ${recipient}. ` +
      `Die persoon meldt zich aan bij Rintintin, claimt het cadeau en boekt bij ${proName}.`,
  }[l];
}

export function claimCtaLabel(view: GiftPublicView, lang?: string | null): string {
  const l = resolveGiftUiLang(lang);
  if (view.product_type === 'credit_pack') {
    if (view.credits_amount != null) {
      return {
        fr: `Ajouter ${view.credits_amount} € de crédits`,
        en: `Add ${view.credits_amount} € in credits`,
        de: `${view.credits_amount} € Guthaben hinzufügen`,
        es: `Añadir ${view.credits_amount} € en créditos`,
        it: `Aggiungi ${view.credits_amount} € di crediti`,
        nl: `${view.credits_amount} € credits toevoegen`,
      }[l];
    }
    return {
      fr: 'Ajouter les crédits à mon compte',
      en: 'Add the credits to my account',
      de: 'Guthaben meinem Konto hinzufügen',
      es: 'Añadir los créditos a mi cuenta',
      it: 'Aggiungi i crediti al mio account',
      nl: 'Credits aan mijn account toevoegen',
    }[l];
  }
  if (view.product_name) {
    return {
      fr: `Réclamer « ${view.product_name} »`,
      en: `Claim “${view.product_name}”`,
      de: `« ${view.product_name} » einlösen`,
      es: `Canjear « ${view.product_name} »`,
      it: `Riscatta « ${view.product_name} »`,
      nl: `« ${view.product_name} » claimen`,
    }[l];
  }
  return {
    fr: 'Réclamer mon forfait',
    en: 'Claim my package',
    de: 'Mein Paket einlösen',
    es: 'Canjear mi bono',
    it: 'Riscatta il mio pacchetto',
    nl: 'Mijn abonnement claimen',
  }[l];
}

export function giftChrome(lang?: string | null) {
  const l = resolveGiftUiLang(lang);
  return {
    forGiving: {
      fr: 'Pour offrir',
      en: 'To give',
      de: 'Zum Verschenken',
      es: 'Para regalar',
      it: 'Da regalare',
      nl: 'Om te geven',
    }[l],
    myGifts: {
      fr: 'Mes cadeaux',
      en: 'My gifts',
      de: 'Meine Gutscheine',
      es: 'Mis regalos',
      it: 'I miei regali',
      nl: 'Mijn cadeaus',
    }[l],
    giftCard: {
      fr: 'Carte cadeau',
      en: 'Gift card',
      de: 'Geschenkkarte',
      es: 'Tarjeta regalo',
      it: 'Buono regalo',
      nl: 'Cadeaukaart',
    }[l],
    received: {
      fr: 'Cadeau reçu',
      en: 'Gift received',
      de: 'Geschenk erhalten',
      es: 'Regalo recibido',
      it: 'Regalo ricevuto',
      nl: 'Cadeau ontvangen',
    }[l],
    activated: {
      fr: 'Cadeau activé',
      en: 'Gift claimed',
      de: 'Geschenk eingelöst',
      es: 'Regalo canjeado',
      it: 'Regalo riscattato',
      nl: 'Cadeau geclaimd',
    }[l],
    otherAccount: {
      fr: 'Autre compte',
      en: 'Wrong account',
      de: 'Anderes Konto',
      es: 'Otra cuenta',
      it: 'Altro account',
      nl: 'Ander account',
    }[l],
    missing: {
      fr: 'Introuvable',
      en: 'Not found',
      de: 'Nicht gefunden',
      es: 'No encontrada',
      it: 'Non trovata',
      nl: 'Niet gevonden',
    }[l],
    missingBody: {
      fr: 'Cette carte n’existe pas ou n’est plus disponible.',
      en: 'This card does not exist or is no longer available.',
      de: 'Diese Karte existiert nicht oder ist nicht mehr verfügbar.',
      es: 'Esta tarjeta no existe o ya no está disponible.',
      it: 'Questa carta non esiste o non è più disponibile.',
      nl: 'Deze kaart bestaat niet of is niet meer beschikbaar.',
    }[l],
    missingCode: {
      fr: 'Code manquant',
      en: 'Missing code',
      de: 'Code fehlt',
      es: 'Código ausente',
      it: 'Codice mancante',
      nl: 'Code ontbreekt',
    }[l],
    giftSent: {
      fr: 'Carte cadeau envoyée',
      en: 'Gift card sent',
      de: 'Geschenkkarte gesendet',
      es: 'Tarjeta regalo enviada',
      it: 'Carta regalo inviata',
      nl: 'Cadeaukaart verstuurd',
    }[l],
    giftSentTo: (email: string) =>
      ({
        fr: `Carte cadeau envoyée à ${email}`,
        en: `Gift card sent to ${email}`,
        de: `Geschenkkarte gesendet an ${email}`,
        es: `Tarjeta regalo enviada a ${email}`,
        it: `Carta regalo inviata a ${email}`,
        nl: `Cadeaukaart verstuurd naar ${email}`,
      })[l],
    giftSentCreditsNote: {
      fr: 'Les crédits n’ont pas été ajoutés à votre compte.',
      en: 'The credits were not added to your account.',
      de: 'Die Guthaben wurden Ihrem Konto nicht gutgeschrieben.',
      es: 'Los créditos no se han añadido a tu cuenta.',
      it: 'I crediti non sono stati aggiunti al tuo account.',
      nl: 'De credits zijn niet aan je account toegevoegd.',
    }[l],
    notYetSent: {
      fr: 'Cette carte n’a pas encore été envoyée.',
      en: 'This gift card has not been sent yet.',
      de: 'Diese Geschenkkarte wurde noch nicht gesendet.',
      es: 'Esta tarjeta aún no se ha enviado.',
      it: 'Questa carta non è ancora stata inviata.',
      nl: 'Deze cadeaukaart is nog niet verstuurd.',
    }[l],
    share: {
      fr: 'Partager',
      en: 'Share',
      de: 'Teilen',
      es: 'Compartir',
      it: 'Condividi',
      nl: 'Delen',
    }[l],
    copyCode: {
      fr: 'Copier le code',
      en: 'Copy the code',
      de: 'Code kopieren',
      es: 'Copiar el código',
      it: 'Copia il codice',
      nl: 'Code kopiëren',
    }[l],
    codeLabel: {
      fr: 'Code',
      en: 'Code',
      de: 'Code',
      es: 'Código',
      it: 'Codice',
      nl: 'Code',
    }[l],
    print: {
      fr: 'Imprimer / PDF',
      en: 'Print / PDF',
      de: 'Drucken / PDF',
      es: 'Imprimir / PDF',
      it: 'Stampa / PDF',
      nl: 'Printen / PDF',
    }[l],
    loginToClaim: {
      fr: 'Se connecter pour réclamer',
      en: 'Sign in to claim',
      de: 'Anmelden zum Einlösen',
      es: 'Iniciar sesión para canjear',
      it: 'Accedi per riscattare',
      nl: 'Aanmelden om te claimen',
    }[l],
    createAccount: {
      fr: 'Créer un compte',
      en: 'Create an account',
      de: 'Konto erstellen',
      es: 'Crear una cuenta',
      it: 'Crea un account',
      nl: 'Account maken',
    }[l],
    switchAccount: {
      fr: 'Utiliser un autre compte',
      en: 'Use another account',
      de: 'Anderes Konto verwenden',
      es: 'Usar otra cuenta',
      it: 'Usa un altro account',
      nl: 'Ander account gebruiken',
    }[l],
    adding: {
      fr: 'Ajout à votre compte…',
      en: 'Adding to your account…',
      de: 'Wird Ihrem Konto hinzugefügt…',
      es: 'Añadiendo a tu cuenta…',
      it: 'Aggiunta all’account…',
      nl: 'Toevoegen aan je account…',
    }[l],
    reserve: {
      fr: 'Réserver',
      en: 'Book',
      de: 'Buchen',
      es: 'Reservar',
      it: 'Prenota',
      nl: 'Boeken',
    }[l],
    seeCredits: {
      fr: 'Voir mes crédits',
      en: 'See my credits',
      de: 'Meine Guthaben ansehen',
      es: 'Ver mis créditos',
      it: 'Vedi i miei crediti',
      nl: 'Mijn credits bekijken',
    }[l],
    seePackages: {
      fr: 'Voir mes forfaits',
      en: 'See my packages',
      de: 'Meine Pakete ansehen',
      es: 'Ver mis bonos',
      it: 'Vedi i miei pacchetti',
      nl: 'Mijn abonnementen bekijken',
    }[l],
    copied: {
      fr: 'Code copié — envoyez-le au bénéficiaire.',
      en: 'Code copied — send it to the recipient.',
      de: 'Code kopiert — senden Sie ihn an die empfangende Person.',
      es: 'Código copiado — envíalo al destinatario.',
      it: 'Codice copiato — invialo al destinatario.',
      nl: 'Code gekopieerd — stuur hem naar de ontvanger.',
    }[l],
    copyFailed: {
      fr: 'Impossible de copier le code.',
      en: 'Could not copy the code.',
      de: 'Code konnte nicht kopiert werden.',
      es: 'No se ha podido copiar el código.',
      it: 'Impossibile copiare il codice.',
      nl: 'Code kon niet worden gekopieerd.',
    }[l],
    yourPro: {
      fr: 'Votre professionnel',
      en: 'Your professional',
      de: 'Ihr Profi',
      es: 'Tu profesional',
      it: 'Il tuo professionista',
      nl: 'Je professional',
    }[l],
    recipientFallback: {
      fr: 'le bénéficiaire',
      en: 'the recipient',
      de: 'die empfangende Person',
      es: 'el destinatario',
      it: 'il destinatario',
      nl: 'de ontvanger',
    }[l],
    seeGift: {
      fr: 'Voir le cadeau',
      en: 'View gift',
      de: 'Geschenk ansehen',
      es: 'Ver el regalo',
      it: 'Vedi il regalo',
      nl: 'Cadeau bekijken',
    }[l],
    inboxTitle: {
      fr: 'Codes cadeau',
      en: 'Gift codes',
      de: 'Gutscheincodes',
      es: 'Códigos regalo',
      it: 'Codici regalo',
      nl: 'Cadeaucodes',
    }[l],
    inboxSubtitle: (email: string) =>
      ({
        fr: `Réclamez un code reçu, ou les cartes envoyées à ${email}.`,
        en: `Claim a code you received, or cards sent to ${email}.`,
        de: `Lösen Sie einen erhaltenen Code ein, oder Karten an ${email}.`,
        es: `Canjea un código recibido, o las tarjetas enviadas a ${email}.`,
        it: `Riscatta un codice ricevuto, o le carte inviate a ${email}.`,
        nl: `Claim een ontvangen code, of kaarten gestuurd naar ${email}.`,
      })[l],
    yourAccount: {
      fr: 'votre compte',
      en: 'your account',
      de: 'Ihr Konto',
      es: 'tu cuenta',
      it: 'il tuo account',
      nl: 'je account',
    }[l],
    receivedCode: {
      fr: 'J’ai reçu un code',
      en: 'I received a code',
      de: 'Ich habe einen Code erhalten',
      es: 'He recibido un código',
      it: 'Ho ricevuto un codice',
      nl: 'Ik heb een code ontvangen',
    }[l],
    claim: {
      fr: 'Réclamer',
      en: 'Claim',
      de: 'Einlösen',
      es: 'Canjear',
      it: 'Riscatta',
      nl: 'Claimen',
    }[l],
    signIn: {
      fr: 'Se connecter',
      en: 'Sign in',
      de: 'Anmelden',
      es: 'Iniciar sesión',
      it: 'Accedi',
      nl: 'Inloggen',
    }[l],
    loginToSee: {
      fr: 'Connectez-vous pour voir vos codes',
      en: 'Sign in to see your codes',
      de: 'Melden Sie sich an, um Ihre Codes zu sehen',
      es: 'Inicia sesión para ver tus códigos',
      it: 'Accedi per vedere i tuoi codici',
      nl: 'Meld je aan om je codes te zien',
    }[l],
    loginToSeeText: {
      fr: 'Les cartes cadeau adressées à votre e-mail apparaissent dans cet espace.',
      en: 'Gift cards sent to your email appear here.',
      de: 'Geschenkkarten an Ihre E-Mail erscheinen hier.',
      es: 'Las tarjetas enviadas a tu correo aparecen aquí.',
      it: 'Le carte inviate alla tua e-mail compaiono qui.',
      nl: 'Cadeaukaarten naar je e-mail verschijnen hier.',
    }[l],
    loadingInbox: {
      fr: 'Chargement de vos cartes cadeau...',
      en: 'Loading your gift cards...',
      de: 'Ihre Geschenkkarten werden geladen...',
      es: 'Cargando tus tarjetas regalo...',
      it: 'Caricamento delle tue carte regalo...',
      nl: 'Je cadeaukaarten worden geladen...',
    }[l],
    loadError: {
      fr: 'Impossible de charger vos cartes cadeau',
      en: 'Could not load your gift cards',
      de: 'Geschenkkarten konnten nicht geladen werden',
      es: 'No se han podido cargar tus tarjetas',
      it: 'Impossibile caricare le tue carte regalo',
      nl: 'Cadeaukaarten konden niet worden geladen',
    }[l],
    tabPending: {
      fr: 'À réclamer',
      en: 'To claim',
      de: 'Einzulösen',
      es: 'Por canjear',
      it: 'Da riscattare',
      nl: 'Te claimen',
    }[l],
    tabRedeemed: {
      fr: 'Sur votre compte',
      en: 'On your account',
      de: 'Auf Ihrem Konto',
      es: 'En tu cuenta',
      it: 'Sul tuo account',
      nl: 'Op je account',
    }[l],
    emptyPending: {
      fr: 'Aucun cadeau à réclamer',
      en: 'No gifts to claim',
      de: 'Keine Geschenke einzulösen',
      es: 'Ningún regalo por canjear',
      it: 'Nessun regalo da riscattare',
      nl: 'Geen cadeaus te claimen',
    }[l],
    emptyPendingText: (email: string) =>
      ({
        fr: `Saisissez un code ci-dessus. Les cartes envoyées à ${email} apparaissent aussi ici.`,
        en: `Enter a code above. Cards sent to ${email} also appear here.`,
        de: `Geben Sie oben einen Code ein. Karten an ${email} erscheinen ebenfalls hier.`,
        es: `Introduce un código arriba. Las tarjetas enviadas a ${email} también aparecen aquí.`,
        it: `Inserisci un codice qui sopra. Le carte inviate a ${email} compaiono anche qui.`,
        nl: `Voer hierboven een code in. Kaarten naar ${email} verschijnen hier ook.`,
      })[l],
    expiredTitle: {
      fr: 'Expirées',
      en: 'Expired',
      de: 'Abgelaufen',
      es: 'Caducadas',
      it: 'Scadute',
      nl: 'Verlopen',
    }[l],
    emptyRedeemed: {
      fr: 'Aucun cadeau sur votre compte',
      en: 'No gifts on your account',
      de: 'Keine Geschenke auf Ihrem Konto',
      es: 'Ningún regalo en tu cuenta',
      it: 'Nessun regalo sul tuo account',
      nl: 'Geen cadeaus op je account',
    }[l],
    emptyRedeemedText: {
      fr: 'Une fois réclamé, le forfait ou les crédits sont prêts pour une réservation.',
      en: 'Once claimed, the package or credits are ready to book.',
      de: 'Nach dem Einlösen sind Paket oder Guthaben bereit zur Buchung.',
      es: 'Una vez canjeado, el bono o los créditos están listos para reservar.',
      it: 'Una volta riscattato, il pacchetto o i crediti sono pronti per prenotare.',
      nl: 'Na het claimen is het abonnement of de credits klaar om te boeken.',
    }[l],
    addingShort: {
      fr: 'Ajout…',
      en: 'Adding…',
      de: 'Wird hinzugefügt…',
      es: 'Añadiendo…',
      it: 'Aggiunta…',
      nl: 'Toevoegen…',
    }[l],
    claimError: {
      fr: 'Impossible de réclamer ce cadeau',
      en: 'Could not claim this gift',
      de: 'Dieses Geschenk konnte nicht eingelöst werden',
      es: 'No se ha podido canjear este regalo',
      it: 'Impossibile riscattare questo regalo',
      nl: 'Dit cadeau kan niet worden geclaimd',
    }[l],
    wrongAccountHint: {
      fr: 'Cette carte est destinée à un autre compte. Connectez-vous avec l’e-mail du bénéficiaire pour la réclamer.',
      en: 'This card is for another account. Sign in with the recipient’s email to claim it.',
      de: 'Diese Karte gehört zu einem anderen Konto. Melden Sie sich mit der E-Mail der empfangenden Person an.',
      es: 'Esta tarjeta es para otra cuenta. Inicia sesión con el correo del destinatario para canjearla.',
      it: 'Questa carta è per un altro account. Accedi con l’e-mail del destinatario per riscattarla.',
      nl: 'Deze kaart is voor een ander account. Log in met het e-mailadres van de ontvanger om te claimen.',
    }[l],
    alreadyUsed: {
      fr: 'Cette carte a déjà été utilisée',
      en: 'This card has already been used',
      de: 'Diese Karte wurde bereits verwendet',
      es: 'Esta tarjeta ya se ha usado',
      it: 'Questa carta è già stata usata',
      nl: 'Deze kaart is al gebruikt',
    }[l],
    expiredCard: {
      fr: 'Cette carte cadeau a expiré',
      en: 'This gift card has expired',
      de: 'Diese Geschenkkarte ist abgelaufen',
      es: 'Esta tarjeta regalo ha caducado',
      it: 'Questa carta regalo è scaduta',
      nl: 'Deze cadeaukaart is verlopen',
    }[l],
    cancelledCard: {
      fr: 'Cette carte cadeau a été annulée',
      en: 'This gift card was cancelled',
      de: 'Diese Geschenkkarte wurde storniert',
      es: 'Esta tarjeta regalo fue cancelada',
      it: 'Questa carta regalo è stata annullata',
      nl: 'Deze cadeaukaart is geannuleerd',
    }[l],
    alreadyOnAccount: {
      fr: 'Ce cadeau est déjà sur votre compte',
      en: 'This gift is already on your account',
      de: 'Dieses Geschenk ist bereits auf Ihrem Konto',
      es: 'Este regalo ya está en tu cuenta',
      it: 'Questo regalo è già sul tuo account',
      nl: 'Dit cadeau staat al op je account',
    }[l],
    claimedReady: {
      fr: 'Le forfait ou les crédits sont prêts. Vous pouvez réserver tout de suite.',
      en: 'The package or credits are ready. You can book right away.',
      de: 'Paket oder Guthaben sind bereit. Sie können jetzt buchen.',
      es: 'El bono o los créditos están listos. Ya puedes reservar.',
      it: 'Il pacchetto o i crediti sono pronti. Puoi prenotare subito.',
      nl: 'Het abonnement of de credits zijn klaar. Je kunt nu boeken.',
    }[l],
    claimedWrongServices: {
      fr: 'Cadeau ajouté à votre compte, mais pas utilisable pour ces services.',
      en: 'Gift added to your account, but it cannot be used for these services.',
      de: 'Geschenk Ihrem Konto hinzugefügt, aber nicht für diese Services nutzbar.',
      es: 'Regalo añadido a tu cuenta, pero no se puede usar para estos servicios.',
      it: 'Regalo aggiunto all’account, ma non utilizzabile per questi servizi.',
      nl: 'Cadeau toegevoegd aan je account, maar niet bruikbaar voor deze diensten.',
    }[l],
    haveGiftCode: {
      fr: 'J’ai un code cadeau',
      en: 'I have a gift code',
      de: 'Ich habe einen Gutscheincode',
      es: 'Tengo un código regalo',
      it: 'Ho un codice regalo',
      nl: 'Ik heb een cadeaucode',
    }[l],
    loginToSeePackages: {
      fr: 'Connectez-vous pour voir vos forfaits',
      en: 'Sign in to see your packages',
      de: 'Melden Sie sich an, um Ihre Pakete zu sehen',
      es: 'Inicia sesión para ver tus bonos',
      it: 'Accedi per vedere i tuoi pacchetti',
      nl: 'Log in om je bundels te zien',
    }[l],
    loginToSeePackagesText: {
      fr: 'Vous devez être connecté pour consulter les séances que vous avez prépayées.',
      en: 'Sign in to see the sessions you have prepaid.',
      de: 'Melden Sie sich an, um Ihre vorausbezahlten Sitzungen zu sehen.',
      es: 'Debes iniciar sesión para ver las sesiones que has prepagado.',
      it: 'Devi accedere per vedere le sessioni che hai prepagato.',
      nl: 'Log in om de sessies te zien die je vooraf hebt betaald.',
    }[l],
    noPackagesYet: {
      fr: 'Aucun forfait pour le moment',
      en: 'No packages yet',
      de: 'Noch keine Pakete',
      es: 'Aún no hay bonos',
      it: 'Nessun pacchetto per il momento',
      nl: 'Nog geen bundels',
    }[l],
    noPackagesYetText: {
      fr: 'Les forfaits regroupent plusieurs séances à un tarif avantageux. Découvrez les éducateurs qui en proposent, ou réclamez un code reçu.',
      en: 'Packages bundle several sessions at a better rate. Browse trainers who offer them, or claim a code you received.',
      de: 'Pakete bündeln mehrere Sitzungen zum Vorteilspreis. Entdecken Sie Trainer, die welche anbieten, oder lösen Sie einen erhaltenen Code ein.',
      es: 'Los bonos agrupan varias sesiones a mejor tarifa. Descubre educadores que los ofrecen, o canjea un código recibido.',
      it: 'I pacchetti raggruppano più sessioni a tariffa vantaggiosa. Scopri gli educatori che li propongono, o riscatta un codice ricevuto.',
      nl: 'Bundels groeperen meerdere sessies tegen een voordelig tarief. Ontdek trainers die ze aanbieden, of claim een ontvangen code.',
    }[l],
    noActivePackages: {
      fr: 'Aucun forfait actif',
      en: 'No active packages',
      de: 'Keine aktiven Pakete',
      es: 'Ningún bono activo',
      it: 'Nessun pacchetto attivo',
      nl: 'Geen actieve bundels',
    }[l],
    noActivePackagesText: {
      fr: 'Tous vos forfaits sont arrivés à expiration. Renouvelez-les pour continuer à profiter de tarifs avantageux.',
      en: 'All your packages have expired. Renew them to keep the better rates.',
      de: 'Alle Ihre Pakete sind abgelaufen. Verlängern Sie sie, um weiter von Vorteilspreisen zu profitieren.',
      es: 'Todos tus bonos han caducado. Renuévalos para seguir con tarifas ventajosas.',
      it: 'Tutti i tuoi pacchetti sono scaduti. Rinnovali per continuare a beneficiare di tariffe vantaggiose.',
      nl: 'Al je bundels zijn verlopen. Vernieuw ze om te blijven profiteren van voordelige tarieven.',
    }[l],
    discoverPros: {
      fr: 'Découvrir nos éducateurs',
      en: 'Browse trainers',
      de: 'Trainer entdecken',
      es: 'Descubrir educadores',
      it: 'Scopri gli educatori',
      nl: 'Ontdek trainers',
    }[l],
    discoverProsAlt: {
      fr: 'Découvrir des éducateurs',
      en: 'Browse trainers',
      de: 'Trainer entdecken',
      es: 'Descubrir educadores',
      it: 'Scopri educatori',
      nl: 'Ontdek trainers',
    }[l],
    reserveSession: {
      fr: 'Réserver une séance',
      en: 'Book a session',
      de: 'Sitzung buchen',
      es: 'Reservar una sesión',
      it: 'Prenota una sessione',
      nl: 'Sessie boeken',
    }[l],
    packageStatusActive: {
      fr: 'Actif',
      en: 'Active',
      de: 'Aktiv',
      es: 'Activo',
      it: 'Attivo',
      nl: 'Actief',
    }[l],
    packageStatusPending: {
      fr: 'En attente',
      en: 'Pending',
      de: 'Ausstehend',
      es: 'Pendiente',
      it: 'In attesa',
      nl: 'In afwachting',
    }[l],
    packageStatusUsedUp: {
      fr: 'Épuisé',
      en: 'Used up',
      de: 'Aufgebraucht',
      es: 'Agotado',
      it: 'Esaurito',
      nl: 'Opgebruikt',
    }[l],
    packageStatusExpired: {
      fr: 'Expiré',
      en: 'Expired',
      de: 'Abgelaufen',
      es: 'Caducado',
      it: 'Scaduto',
      nl: 'Verlopen',
    }[l],
    packageStatusCanceled: {
      fr: 'Annulé',
      en: 'Canceled',
      de: 'Storniert',
      es: 'Cancelado',
      it: 'Annullato',
      nl: 'Geannuleerd',
    }[l],
    remainingLabel: {
      fr: 'restantes',
      en: 'left',
      de: 'übrig',
      es: 'restantes',
      it: 'rimanenti',
      nl: 'resterend',
    }[l],
    quotaExhausted: {
      fr: 'Quota épuisé',
      en: 'Quota used up',
      de: 'Kontingent aufgebraucht',
      es: 'Cupo agotado',
      it: 'Quota esaurita',
      nl: 'Quota opgebruikt',
    }[l],
    sessionsAvailable: (n: number) =>
      ({
        fr: n === 1 ? '1 séance disponible' : `${n} séances disponibles`,
        en: n === 1 ? '1 session available' : `${n} sessions available`,
        de: n === 1 ? '1 Sitzung verfügbar' : `${n} Sitzungen verfügbar`,
        es: n === 1 ? '1 sesión disponible' : `${n} sesiones disponibles`,
        it: n === 1 ? '1 sessione disponibile' : `${n} sessioni disponibili`,
        nl: n === 1 ? '1 sessie beschikbaar' : `${n} sessies beschikbaar`,
      })[l],
    includedServices: {
      fr: 'Services inclus',
      en: 'Included services',
      de: 'Enthaltene Services',
      es: 'Servicios incluidos',
      it: 'Servizi inclusi',
      nl: 'Inbegrepen diensten',
    }[l],
    expiredOn: (date: string) =>
      ({
        fr: `Expiré le ${date}`,
        en: `Expired on ${date}`,
        de: `Abgelaufen am ${date}`,
        es: `Caducado el ${date}`,
        it: `Scaduto il ${date}`,
        nl: `Verlopen op ${date}`,
      })[l],
    expiringSoon: (days: number, date: string) =>
      ({
        fr:
          days === 1
            ? `Plus que 1 jour pour réserver — date limite ${date}`
            : `Plus que ${days} jours pour réserver — date limite ${date}`,
        en:
          days === 1
            ? `Only 1 day left to book — deadline ${date}`
            : `Only ${days} days left to book — deadline ${date}`,
        de:
          days === 1
            ? `Nur noch 1 Tag zum Buchen — Frist ${date}`
            : `Nur noch ${days} Tage zum Buchen — Frist ${date}`,
        es:
          days === 1
            ? `Solo queda 1 día para reservar — fecha límite ${date}`
            : `Solo quedan ${days} días para reservar — fecha límite ${date}`,
        it:
          days === 1
            ? `Solo 1 giorno per prenotare — scadenza ${date}`
            : `Solo ${days} giorni per prenotare — scadenza ${date}`,
        nl:
          days === 1
            ? `Nog 1 dag om te boeken — deadline ${date}`
            : `Nog ${days} dagen om te boeken — deadline ${date}`,
      })[l],
    validUntil: (date: string) =>
      ({
        fr: `Valable jusqu'au ${date}`,
        en: `Valid until ${date}`,
        de: `Gültig bis ${date}`,
        es: `Válido hasta el ${date}`,
        it: `Valido fino al ${date}`,
        nl: `Geldig tot ${date}`,
      })[l],
    sectionActivePackages: {
      fr: 'Forfaits actifs',
      en: 'Active packages',
      de: 'Aktive Pakete',
      es: 'Bonos activos',
      it: 'Pacchetti attivi',
      nl: 'Actieve bundels',
    }[l],
    sectionExpiredPackages: {
      fr: 'Forfaits expirés',
      en: 'Expired packages',
      de: 'Abgelaufene Pakete',
      es: 'Bonos caducados',
      it: 'Pacchetti scaduti',
      nl: 'Verlopen bundels',
    }[l],
    loadingPackages: {
      fr: 'Chargement de vos forfaits...',
      en: 'Loading your packages...',
      de: 'Ihre Pakete werden geladen...',
      es: 'Cargando tus bonos...',
      it: 'Caricamento dei tuoi pacchetti...',
      nl: 'Je bundels laden...',
    }[l],
    errorLoadPackages: {
      fr: 'Erreur lors du chargement de vos forfaits',
      en: 'Could not load your packages',
      de: 'Pakete konnten nicht geladen werden',
      es: 'No se pudieron cargar tus bonos',
      it: 'Impossibile caricare i tuoi pacchetti',
      nl: 'Je bundels konden niet worden geladen',
    }[l],
    statActivePackages: (n: number) =>
      ({
        fr: n === 1 ? 'Forfait actif' : 'Forfaits actifs',
        en: n === 1 ? 'Active package' : 'Active packages',
        de: n === 1 ? 'Aktives Paket' : 'Aktive Pakete',
        es: n === 1 ? 'Bono activo' : 'Bonos activos',
        it: n === 1 ? 'Pacchetto attivo' : 'Pacchetti attivi',
        nl: n === 1 ? 'Actieve bundel' : 'Actieve bundels',
      })[l],
    statSessionsAvailable: (n: number) =>
      ({
        fr: n === 1 ? 'Séance disponible' : 'Séances disponibles',
        en: n === 1 ? 'Session available' : 'Sessions available',
        de: n === 1 ? 'Sitzung verfügbar' : 'Sitzungen verfügbar',
        es: n === 1 ? 'Sesión disponible' : 'Sesiones disponibles',
        it: n === 1 ? 'Sessione disponibile' : 'Sessioni disponibili',
        nl: n === 1 ? 'Sessie beschikbaar' : 'Sessies beschikbaar',
      })[l],
    statPros: (n: number) =>
      ({
        fr: n === 1 ? 'Éducateur' : 'Éducateurs',
        en: n === 1 ? 'Professional' : 'Professionals',
        de: n === 1 ? 'Trainer' : 'Trainer',
        es: n === 1 ? 'Educador' : 'Educadores',
        it: n === 1 ? 'Educatore' : 'Educatori',
        nl: n === 1 ? 'Professional' : 'Professionals',
      })[l],
    serviceUsageAria: (name: string, used: number, total: number) =>
      ({
        fr: `${name} : ${used} utilisées sur ${total}`,
        en: `${name}: ${used} used of ${total}`,
        de: `${name}: ${used} von ${total} genutzt`,
        es: `${name}: ${used} usadas de ${total}`,
        it: `${name}: ${used} usate su ${total}`,
        nl: `${name}: ${used} gebruikt van ${total}`,
      })[l],
    sessionsLeft: (n: number) =>
      ({
        fr: n === 1 ? 'Il vous reste 1 séance sur ce forfait.' : `Il vous reste ${n} séances sur ce forfait.`,
        en: n === 1 ? 'You have 1 session left on this package.' : `You have ${n} sessions left on this package.`,
        de: n === 1 ? 'Sie haben noch 1 Sitzung in diesem Paket.' : `Sie haben noch ${n} Sitzungen in diesem Paket.`,
        es: n === 1 ? 'Te queda 1 sesión en este bono.' : `Te quedan ${n} sesiones en este bono.`,
        it: n === 1 ? 'Ti resta 1 sessione in questo pacchetto.' : `Ti restano ${n} sessioni in questo pacchetto.`,
        nl: n === 1 ? 'Je hebt nog 1 sessie in deze bundel.' : `Je hebt nog ${n} sessies in deze bundel.`,
      })[l],
    noSessionsLeft: {
      fr: 'Toutes les séances de ce forfait ont été utilisées.',
      en: 'All sessions on this package have been used.',
      de: 'Alle Sitzungen dieses Pakets wurden genutzt.',
      es: 'Se han usado todas las sesiones de este bono.',
      it: 'Tutte le sessioni di questo pacchetto sono state usate.',
      nl: 'Alle sessies van deze bundel zijn gebruikt.',
    }[l],
    noCreditsLeft: {
      fr: 'Tous les crédits de cette carte ont été utilisés.',
      en: 'All credits from this gift have been used.',
      de: 'Das gesamte Guthaben dieser Karte wurde genutzt.',
      es: 'Se han usado todos los créditos de esta tarjeta.',
      it: 'Tutti i crediti di questa carta sono stati usati.',
      nl: 'Alle credits van deze kaart zijn gebruikt.',
    }[l],
    creditsLeft: (n: number) =>
      ({
        fr: n === 1 ? 'Il vous reste 1 crédit.' : `Il vous reste ${n} crédits.`,
        en: n === 1 ? 'You have 1 credit left.' : `You have ${n} credits left.`,
        de: n === 1 ? 'Sie haben noch 1 Guthaben.' : `Sie haben noch ${n} Guthaben.`,
        es: n === 1 ? 'Te queda 1 crédito.' : `Te quedan ${n} créditos.`,
        it: n === 1 ? 'Ti resta 1 credito.' : `Ti restano ${n} crediti.`,
        nl: n === 1 ? 'Je hebt nog 1 credit.' : `Je hebt nog ${n} credits.`,
      })[l],
    useCredits: {
      fr: 'Utiliser mes crédits',
      en: 'Use my credits',
      de: 'Guthaben nutzen',
      es: 'Usar mis créditos',
      it: 'Usa i miei crediti',
      nl: 'Credits gebruiken',
    }[l],
    includedInPackage: {
      fr: 'Inclus dans votre forfait',
      en: 'Included in your package',
      de: 'In Ihrem Paket enthalten',
      es: 'Incluido en tu bono',
      it: 'Incluso nel tuo pacchetto',
      nl: 'Inbegrepen in je abonnement',
    }[l],
    includedShort: {
      fr: 'Inclus',
      en: 'Included',
      de: 'Inklusive',
      es: 'Incluido',
      it: 'Incluso',
      nl: 'Inbegrepen',
    }[l],
    free: {
      fr: 'Gratuit',
      en: 'Free',
      de: 'Kostenlos',
      es: 'Gratis',
      it: 'Gratis',
      nl: 'Gratis',
    }[l],
    creditsCount: (n: number) =>
      ({
        fr: n === 1 ? '1 crédit' : `${n} crédits`,
        en: n === 1 ? '1 credit' : `${n} credits`,
        de: n === 1 ? '1 Guthaben' : `${n} Guthaben`,
        es: n === 1 ? '1 crédito' : `${n} créditos`,
        it: n === 1 ? '1 credito' : `${n} crediti`,
        nl: n === 1 ? '1 credit' : `${n} credits`,
      })[l],
    walletPaysWithPackage: {
      fr: 'Votre forfait sera utilisé pour cette séance.',
      en: 'Your package will be used for this session.',
      de: 'Ihr Paket wird für diese Sitzung verwendet.',
      es: 'Tu bono se usará para esta sesión.',
      it: 'Il tuo pacchetto verrà usato per questa sessione.',
      nl: 'Je abonnement wordt voor deze sessie gebruikt.',
    }[l],
    walletPaysWithCredits: {
      fr: 'Vos crédits seront utilisés pour cette séance.',
      en: 'Your credits will be used for this session.',
      de: 'Ihr Guthaben wird für diese Sitzung verwendet.',
      es: 'Tus créditos se usarán para esta sesión.',
      it: 'I tuoi crediti verranno usati per questa sessione.',
      nl: 'Je credits worden voor deze sessie gebruikt.',
    }[l],
    packageAppliesHint: {
      fr: 'Votre forfait s’applique aux services marqués « Inclus dans votre forfait ».',
      en: 'Your package applies to services marked “Included in your package”.',
      de: 'Ihr Paket gilt für Leistungen mit dem Hinweis „In Ihrem Paket enthalten“.',
      es: 'Tu bono se aplica a los servicios marcados «Incluido en tu bono».',
      it: 'Il tuo pacchetto vale per i servizi contrassegnati «Incluso nel tuo pacchetto».',
      nl: 'Je abonnement geldt voor diensten met «Inbegrepen in je abonnement».',
    }[l],
    creditsAtPro: (amount: number, proName: string) =>
      ({
        fr: `Vous avez ${amount} € de crédits chez ${proName}.`,
        en: `You have €${amount} in credits at ${proName}.`,
        de: `Sie haben ${amount} € Guthaben bei ${proName}.`,
        es: `Tienes ${amount} € de créditos en ${proName}.`,
        it: `Hai ${amount} € di crediti da ${proName}.`,
        nl: `Je hebt €${amount} credits bij ${proName}.`,
      })[l],
    creditsAvailable: (amount: number) =>
      ({
        fr: `Vous avez ${amount} crédits disponibles`,
        en: `You have ${amount} credits available`,
        de: `Sie haben ${amount} Guthaben verfügbar`,
        es: `Tienes ${amount} créditos disponibles`,
        it: `Hai ${amount} crediti disponibili`,
        nl: `Je hebt ${amount} credits beschikbaar`,
      })[l],
    creditsShort: (have: number, need: number) =>
      ({
        fr: `Solde : ${have} crédits (il vous manque ${need})`,
        en: `Balance: ${have} credits (you need ${need} more)`,
        de: `Stand: ${have} Guthaben (es fehlen ${need})`,
        es: `Saldo: ${have} créditos (te faltan ${need})`,
        it: `Saldo: ${have} crediti (ne mancano ${need})`,
        nl: `Saldo: ${have} credits (je mist er ${need})`,
      })[l],
    atPro: (name: string) =>
      ({
        fr: `Chez ${name}`,
        en: `At ${name}`,
        de: `Bei ${name}`,
        es: `En ${name}`,
        it: `Da ${name}`,
        nl: `Bij ${name}`,
      })[l],
    destinedTo: (name: string) =>
      ({
        fr: 'Destiné à',
        en: 'For',
        de: 'Bestimmt für',
        es: 'Destinado a',
        it: 'Destinato a',
        nl: 'Bestemd voor',
      })[l] + (name ? ` ${name}` : ''),
  };
}

export function walletFallbackPath(productType: GiftProductType): string {
  return productType === 'credit_pack' ? '/credits' : '/mes-forfaits';
}

export function giftReservePath(proSlug?: string | null): string | null {
  const slug = String(proSlug || '').trim();
  return slug ? `/pro/${slug}?booking=true` : null;
}

export function giftOwnedWalletState(data?: {
  remaining_sessions?: number | null;
  credits_remaining?: number | null;
} | null): {
  offerReserve: boolean;
  sessions: number | null;
  credits: number | null;
} {
  const sessions =
    typeof data?.remaining_sessions === 'number' ? data.remaining_sessions : null;
  const credits =
    typeof data?.credits_remaining === 'number' ? data.credits_remaining : null;
  const exhausted =
    (sessions !== null && sessions <= 0) || (credits !== null && credits <= 0);
  return { offerReserve: !exhausted, sessions, credits };
}

export function giftWalletRemainingCopy(
  state: ReturnType<typeof giftOwnedWalletState>,
  lang?: string | null
): string | null {
  const chrome = giftChrome(lang);
  if (state.sessions !== null) {
    return state.sessions <= 0 ? chrome.noSessionsLeft : chrome.sessionsLeft(state.sessions);
  }
  if (state.credits !== null) {
    return state.credits <= 0 ? chrome.noCreditsLeft : chrome.creditsLeft(state.credits);
  }
  return null;
}

/** false seulement si l’API a envoyé un solde à 0 — sinon on garde Réserver. */
export function giftClaimOffersReserve(result?: {
  client_view?: Record<string, unknown> | null;
} | null): boolean {
  return giftOwnedWalletState(
    result?.client_view as {
      remaining_sessions?: number | null;
      credits_remaining?: number | null;
    } | null
  ).offerReserve;
}

export function giftNextCtaLabel(
  next?: { use_label?: string | null } | null,
  lang?: string | null
): string {
  const l = resolveGiftUiLang(lang);
  return (l === 'fr' && next?.use_label) || giftChrome(l).reserve;
}

export function giftWalletCtaLabel(
  next?: { wallet_label?: string | null } | null,
  productType?: string,
  lang?: string | null
): string {
  const l = resolveGiftUiLang(lang);
  if (l === 'fr' && next?.wallet_label) return next.wallet_label;
  const chrome = giftChrome(l);
  return productType === 'credit_pack' ? chrome.seeCredits : chrome.seePackages;
}

export function giftCodePath(code: string, opts?: { activate?: boolean; print?: boolean }): string {
  const params = new URLSearchParams();
  if (opts?.activate) params.set('activate', '1');
  if (opts?.print) params.set('print', '1');
  const qs = params.toString();
  return `/cadeau/${encodeURIComponent(code)}${qs ? `?${qs}` : ''}`;
}

const NATIVE_GIFT_HOSTS = new Set([
  'app.rintintin.io',
  'app.rintintin-booking.com',
  'rintintin-booking.com',
  'www.rintintin-booking.com',
  'localhost',
  '127.0.0.1',
]);

const GIFT_CODE_RE = /^[A-Za-z0-9][A-Za-z0-9-]{1,63}$/;

/** Deep link natif (scheme ou Universal Link) → chemin in-app `/cadeau/:code`. */
export function parseNativeGiftOpenUrl(rawUrl: string): string | null {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  try {
    let pathname = '';
    let search = '';
    if (rawUrl.startsWith('rintintin://')) {
      const rest = rawUrl.slice('rintintin://'.length);
      const qIndex = rest.indexOf('?');
      const pathPart = qIndex >= 0 ? rest.slice(0, qIndex) : rest;
      search = qIndex >= 0 ? rest.slice(qIndex + 1) : '';
      pathname = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
    } else {
      const url = new URL(rawUrl);
      if (!NATIVE_GIFT_HOSTS.has(url.hostname)) return null;
      pathname = url.pathname;
      search = url.search.startsWith('?') ? url.search.slice(1) : url.search;
    }
    const match = pathname.match(/^\/cadeau\/([^/]+)$/i);
    if (!match) return null;
    const code = decodeURIComponent(match[1]).trim();
    if (!GIFT_CODE_RE.test(code)) return null;
    const params = new URLSearchParams(search);
    return giftCodePath(code, {
      activate: params.get('activate') === '1',
      print: params.get('print') === '1',
    });
  } catch {
    return null;
  }
}

export function giftPublicLink(view: GiftPublicView): string {
  if (view.public_url) return view.public_url;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/cadeau/${encodeURIComponent(view.code)}`;
  }
  return giftCodePath(view.code);
}

export function giftSharePayload(
  view: GiftPublicView,
  lang?: string | null
): {
  title: string;
  text: string;
  url: string;
} {
  const l = resolveGiftUiLang(lang);
  const product = view.product_name || {
    fr: 'un cadeau Rintintin',
    en: 'a Rintintin gift',
    de: 'ein Rintintin-Geschenk',
    es: 'un regalo Rintintin',
    it: 'un regalo Rintintin',
    nl: 'een Rintintin-cadeau',
  }[l];
  const url = giftPublicLink(view);
  const recipient = String(view.recipient_name || '').trim();
  const title = recipient
    ? {
        fr: `Cadeau pour ${recipient}`,
        en: `Gift for ${recipient}`,
        de: `Geschenk für ${recipient}`,
        es: `Regalo para ${recipient}`,
        it: `Regalo per ${recipient}`,
        nl: `Cadeau voor ${recipient}`,
      }[l]
    : {
        fr: 'Carte cadeau Rintintin',
        en: 'Rintintin gift card',
        de: 'Rintintin-Geschenkkarte',
        es: 'Tarjeta regalo Rintintin',
        it: 'Buono regalo Rintintin',
        nl: 'Rintintin-cadeaukaart',
      }[l];
  const howto = {
    fr: 'Le bénéficiaire se connecte, réclame le cadeau, puis réserve.',
    en: 'The recipient signs in, claims the gift, then books.',
    de: 'Die empfangende Person meldet sich an, löst das Geschenk ein und bucht.',
    es: 'El destinatario inicia sesión, canjea el regalo y reserva.',
    it: 'Il destinatario accede, riscatta il regalo e prenota.',
    nl: 'De ontvanger meldt zich aan, claimt het cadeau en boekt.',
  }[l];
  const codeLabel = {
    fr: 'Code',
    en: 'Code',
    de: 'Code',
    es: 'Código',
    it: 'Codice',
    nl: 'Code',
  }[l];
  const text = [
    recipient ? `${recipient} : ${product}` : product,
    `${codeLabel} : ${view.code}`,
    howto,
  ].join('\n');
  return { title, text, url };
}

export function giftShareClipboardText(view: GiftPublicView, lang?: string | null): string {
  const payload = giftSharePayload(view, lang);
  return `${payload.text}\n${payload.url}`;
}

export function canShareGiftCard(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/** Impression auto après achat : desktop seulement. Sur téléphone, Partager / copier. */
export function shouldAutoPrintGiftCard(opts: {
  native?: boolean;
  width?: number;
} = {}): boolean {
  if (opts.native) return false;
  const width =
    typeof opts.width === 'number'
      ? opts.width
      : typeof window !== 'undefined'
        ? window.innerWidth
        : 0;
  return width >= 768;
}

export function giftNextFromClaim(result?: {
  client_view?: Record<string, unknown> | null;
} | null): GiftNextSteps | null {
  const next = result?.client_view?.next;
  if (!next || typeof next !== 'object') return null;
  const steps = { ...(next as GiftNextSteps) };
  if (!giftClaimOffersReserve(result)) {
    steps.use_path = null;
  }
  if (!steps.use_path && !steps.wallet_path) return null;
  return steps;
}

export function giftClaimFollowUp(
  result?: {
    message?: string;
    product_name?: string;
    product_type?: string;
    client_view?: Record<string, unknown> | null;
  } | null,
  lang?: string | null
): { message: string; path: string | null; label: string } {
  const l = resolveGiftUiLang(lang);
  const localized = {
    fr: 'Cadeau ajouté à votre compte',
    en: 'Gift added to your account',
    de: 'Geschenk Ihrem Konto hinzugefügt',
    es: 'Regalo añadido a tu cuenta',
    it: 'Regalo aggiunto al tuo account',
    nl: 'Cadeau toegevoegd aan je account',
  }[l];
  const apiMessage = String(result?.message || '').trim();
  const genericApi =
    !apiMessage ||
    /cadeau ajouté|crédits ajoutés|forfait activé grâce/i.test(apiMessage);
  const message = genericApi
    ? result?.product_name || (l === 'fr' && apiMessage) || localized
    : apiMessage;
  const next = giftNextFromClaim(result);
  const pro = result?.client_view?.pro;
  const slug =
    pro && typeof pro === 'object' ? String((pro as { slug?: string }).slug || '') : '';
  if (!giftClaimOffersReserve(result)) {
    const viewType = (result?.client_view as { product_type?: string } | undefined)?.product_type;
    const productType = String(result?.product_type || viewType || '');
    const kind = productType === 'credit_pack' ? 'credit_pack' : 'forfait';
    return {
      message,
      path: next?.wallet_path || walletFallbackPath(kind),
      label: giftWalletCtaLabel(next, kind, l),
    };
  }
  return {
    message,
    path: next?.use_path || giftReservePath(slug),
    label: giftNextCtaLabel(next, l),
  };
}

export function localizeGiftApiError(
  message?: string | null,
  lang?: string | null
): string {
  const chrome = giftChrome(lang);
  const text = String(message || '').trim();
  if (!text) return chrome.claimError;
  if (/autre compte|another account/i.test(text)) return chrome.wrongAccountHint;
  if (/déjà (été )?utilis|already (been )?used/i.test(text)) return chrome.alreadyUsed;
  if (/expir/i.test(text)) return chrome.expiredCard;
  if (/annul|cancel/i.test(text)) return chrome.cancelledCard;
  if (/déjà sur votre compte|already on your account/i.test(text)) {
    return chrome.alreadyOnAccount;
  }
  if (/introuvable|not found|n'existe pas|no longer available/i.test(text)) {
    return chrome.missingBody;
  }
  if (/pas encore (été )?envoy|not (yet )?been sent|has not been sent yet/i.test(text)) {
    return chrome.notYetSent;
  }
  if (
    /code manquant|missing code|code fehlt|código ausente|codice mancante|code ontbreekt/i.test(
      text
    )
  ) {
    return chrome.missingCode;
  }
  return text;
}

export function giftErrorFromUnknown(err: unknown): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string };
  const api = String(e.response?.data?.error || '').trim();
  if (api) return api;
  const msg = String(e.message || '').trim();
  if (/code manquant|missing code/i.test(msg)) return msg;
  return '';
}
